/** @jsxImportSource @emotion/react */

import { useMutation, useQuery } from '@apollo/client'
import Modal from '@mui/material/Modal'
import Typography from '@mui/material/Typography'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { bulkUpdateTmLink, bulkUpdateTmLinkVariables } from '__generatedTypes__/bulkUpdateTmLink'
import {
  findAllTeamMember_findAllTeamMember_items as TeamMember,
  findAllTeamMember_findAllTeamMember_items_link,
} from '__generatedTypes__/findAllTeamMember'
import { UpdateTeamMemberInput } from '__generatedTypes__/globalTypes'
import { removeTeamMember, removeTeamMemberVariables } from '__generatedTypes__/removeTeamMember'
import { removeTmLink, removeTmLinkVariables } from '__generatedTypes__/removeTmLink'
import {
  searchTeamMember,
  searchTeamMember_searchTeamMember_items as SearchedTeamMember,
  searchTeamMemberVariables,
} from '__generatedTypes__/searchTeamMember'
import { updateTeamMember, updateTeamMemberVariables } from '__generatedTypes__/updateTeamMember'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import Table, { TableProps } from 'components/Table'
import {
  BULK_UPDATE_TEAM_MEMBERS_LINK,
  DELETE_TEAM_MEMBER,
  REMOVE_TEAM_MEMBERS_LINK,
  SEARCH_TEAM_MEMBER,
  UPDATE_TEAM_MEMBER,
} from 'queries/teamMember'
import { toast } from 'utils/toast'
import ManageTeamMemberModal, { DeletionError, handleServerErrors } from '../ManageTeamMemberModal'
import { handleDeletionError } from '../TeamMembersList/TeamMembersList'
import * as classes from './styles'
import TeamMembersSearch from './TeamMembersSearch'

interface IProps {
  platformId: string
  platformSlug: string
}

export interface TeamMemberLink extends TeamMember {
  platformLink?: findAllTeamMember_findAllTeamMember_items_link
  index: number
}

const TeamMembersPlatformList: FC<IProps> = ({ platformId, platformSlug }) => {
  const [teamMembersIndexed, setTeamMembersIndexed] = useState<Array<TeamMemberLink>>([])
  const [updateTeamMember] = useMutation<updateTeamMember, updateTeamMemberVariables>(UPDATE_TEAM_MEMBER)
  const [removeTmLink] = useMutation<removeTmLink, removeTmLinkVariables>(REMOVE_TEAM_MEMBERS_LINK)
  const [bulkUpdateTmLink] = useMutation<bulkUpdateTmLink, bulkUpdateTmLinkVariables>(BULK_UPDATE_TEAM_MEMBERS_LINK)
  const [isTeamMemberModalOpen, setTeamMemberModalOpen] = useState<boolean>(false)
  const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMemberLink | SearchedTeamMember | null>(null)
  const [deletionError, setDeletionError] = useState<DeletionError | null>(null)

  useEffect(() => {
    setDeletionError(null)
  }, [selectedTeamMember])

  const [removeTeamMember, { loading: removeLoading }] = useMutation<removeTeamMember, removeTeamMemberVariables>(
    DELETE_TEAM_MEMBER
  )

  const { data, refetch } = useQuery<searchTeamMember, searchTeamMemberVariables>(SEARCH_TEAM_MEMBER, {
    variables: {
      isPresentPlatformSlug: platformSlug,
    },
  })

  useEffect(() => {
    setTeamMembersIndexed(
      data?.searchTeamMember.items.map((item, index) => ({
        ...item,
        platformLink: item.link.find((link) => link.platform.id === platformId),
        index,
      })) || []
    )
  }, [platformId, data?.searchTeamMember.items])

  const handleEditClick = (member: TeamMemberLink | SearchedTeamMember) => {
    setTeamMemberModalOpen(true)
    setSelectedTeamMember(member)
  }

  const handleModalClose = () => {
    setTeamMemberModalOpen(false)
    setSelectedTeamMember(null)
  }

  const handleTeamMemberDelete = useCallback(async (selectedTeamMemberId: string) => {
    try {
      await removeTeamMember({ variables: { params: selectedTeamMemberId } })
      await refetch()
      handleModalClose()
      toast.success('Team member deleted successfully')
    } catch (error) {
      setDeletionError(handleDeletionError(error))
      toast.error('Failed to delete team member')
    }
  }, [])

  const handleTeamMemberUpdate = async (teamMemberProps: UpdateTeamMemberInput) => {
    try {
      await updateTeamMember({
        variables: {
          params: {
            id: selectedTeamMember?.id,
            ...teamMemberProps,
          },
        },
      })

      await refetch()
      toast.success('Team member updated successfully')
    } catch (error) {
      toast.error('Failed to update team member')
      return handleServerErrors(error)
    }
  }

  const handleTeamMemberClickDelete = async (member: TeamMemberLink) => {
    if (member.platformLink) {
      try {
        await removeTmLink({ variables: { removeTmLinkId: member.platformLink.id } })
        await refetch()
        toast.success('Team member deleted successfully')
      } catch (error) {
        toast.error('Failed to delete team member')
      }
    }
  }

  const TeamMemberColumns: TableProps<TeamMemberLink>['columns'] = [
    {
      renderCell: (member) => (
        <Typography variant="body2" css={classes.index}>
          {member.index + 1}
        </Typography>
      ),
      flex: '0 1 45px',
    },
    {
      renderCell: (member) => (
        <div css={classes.avatar}>
          <img css={classes.avatar} src={member.getAvatar.photo || ''} alt="Team member avatar" />
        </div>
      ),
      flex: '0 1 30px',
    },
    {
      renderCell: (member) => (
        <Typography variant="body2" css={classes.longText}>
          {member.fullName}
        </Typography>
      ),
    },
    {
      renderCell: (member) => <CrossIcon css={classes.editIcon} onClick={() => handleTeamMemberClickDelete(member)} />,
      flex: '0 1 29px',
    },
    {
      renderCell: (member) => <EditIcon css={classes.editIcon} onClick={() => handleEditClick(member)} />,
      flex: '0 1 29px',
    },
  ]

  const onSort = useCallback(
    async (items: Array<TeamMemberLink>) => {
      if (items.length > 0) {
        try {
          setTeamMembersIndexed(items)

          await bulkUpdateTmLink({
            variables: {
              bulkUpdateTmLinkInput: items.map((item, order) => ({ id: item.platformLink?.id, order })),
            },
          })

          await refetch()
          toast.success('Team members order updated successfully')
        } catch (error) {
          toast.error('Failed to update team members order')
        }
      }
    },
    [setTeamMembersIndexed]
  )

  return (
    <div css={classes.container}>
      <TeamMembersSearch
        teamMembersIndexedLength={teamMembersIndexed.length}
        platformId={platformId}
        refetch={refetch}
        onEditClick={handleEditClick}
        addedTeamMemberIds={teamMembersIndexed.map(({ id }) => id)}
      />
      <Typography variant="body2" css={classes.tableTitle}>
        Order of Team Members
      </Typography>
      {teamMembersIndexed.length > 0 && (
        <Table
          css={classes.table}
          columns={TeamMemberColumns}
          items={teamMembersIndexed}
          hideHeader
          isSortable
          onSort={onSort}
        />
      )}
      <Modal open={isTeamMemberModalOpen} onClose={handleModalClose}>
        <ManageTeamMemberModal
          onClose={handleModalClose}
          selectedTeamMember={selectedTeamMember}
          onTeamMemberDelete={handleTeamMemberDelete}
          onTeamMemberSubmit={handleTeamMemberUpdate}
          removeLoading={removeLoading}
          deletionError={deletionError}
        />
      </Modal>
    </div>
  )
}

export default TeamMembersPlatformList
